import React, { FC, useEffect, useMemo, useState } from "react"
import { KButton, KSpan, KTitleSpan } from "kahuna-base-react-components"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from "chart.js"
import { Line } from "react-chartjs-2"
import { handleAuthorizedRequest, users } from "../../../../../apis"
import { mapToCamelCase, addCommasToNumber, yyyymmToDate, replaceCurrencies } from "../../../../../utility"
import { AdvanceDealDetail, AdvanceDealRow, DealDetailMonth, UserAdvanceProgressValues } from "../../../../../types"
import Loading from "../../../../ui/Loading"
import {
  buildTranches,
  trancheEconomicMoic,
  windowedIrr,
  annualizedIrr,
  xirr as trancheXirr,
  Tranche
} from "./advanceTrancheBreakdown"

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend)

const PURPLE = "#7c3aed"
const GREEN = "#22c55e"
const AMBER = "#f59e0b"
const RED = "#ef4444"
const TEXT_MUTED = "#6b7280"
const TEXT_PRIMARY = "#111"
const CHART_FONT = "Inter var, Inter, system-ui, -apple-system, sans-serif"

type AdvanceDealDetailPageProps = {
  selectedDeal: AdvanceDealRow
  onBack: () => void
}

// ---- formatting -------------------------------------------------------------

const usd0 = (value: number | null | undefined) =>
  value === null || value === undefined || isNaN(value) ? "N/A" : `$${addCommasToNumber(Math.round(value))}`

const compactUsd = (value: number) => {
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 1_000) return `$${Math.round(value / 1_000)}K`
  return `$${Math.round(value)}`
}

const pct = (value: number | null | undefined, digits = 0) =>
  value === null || value === undefined || isNaN(value) ? "N/A" : `${parseFloat(value.toFixed(digits))}%`

const multipleX = (value: number | null | undefined) =>
  value === null || value === undefined || isNaN(value) ? "N/A" : `${value.toFixed(2)}x`

const xirrPct = (fraction: number | null | undefined) =>
  fraction === null || fraction === undefined || isNaN(fraction) ? "N/A" : `${(fraction * 100).toFixed(1)}%`

const monthYear = (key: string | null | undefined) => {
  if (!key) return "N/A"
  const year = key.slice(0, 4)
  const month = parseInt(key.slice(4, 6), 10)
  if (!year || isNaN(month)) return "N/A"
  return new Date(parseInt(year, 10), month - 1, 1).toLocaleDateString("en-US", { month: "short", year: "numeric" })
}

const monthYearShort = (key: string) => {
  const year = key.slice(2, 4)
  const month = parseInt(key.slice(4, 6), 10)
  if (isNaN(month)) return key
  return `${new Date(2000, month - 1, 1).toLocaleDateString("en-US", { month: "short" })} ${year}`
}

// A freshly funded advance has no reported royalties and, until the first projection run,
// no projections either. Object.keys(...).reduce() with no seed throws on an empty array,
// which is what took the whole detail page down for newly funded artists.
const latestMonth = (months: { [key: string]: number } | null | undefined): string | null => {
  const keys = Object.keys(months || {})
  return keys.length === 0 ? null : keys.reduce((a, b) => (a > b ? a : b))
}

const monthKeys = (months: { [key: string]: number } | null | undefined): string[] => Object.keys(months || {})

const sortedUnion = (...maps: ({ [key: string]: number } | null | undefined)[]): string[] => {
  const seen = new Set<string>()
  maps.forEach((map) => Object.keys(map || {}).forEach((month) => seen.add(month)))
  return Array.from(seen).sort()
}

const monthsText = (value: number | null | undefined) =>
  value === null || value === undefined || isNaN(value) ? "N/A" : `${Math.round(value)} months`

const timingText = (value: number | null | undefined) =>
  value === null || value === undefined || isNaN(value)
    ? "N/A"
    : `${value > 0 ? "+" : ""}${Math.round(value)} mo`

const addMonthsToKey = (key: string, months: number) => {
  const year = parseInt(key.slice(0, 4), 10)
  const month = parseInt(key.slice(4, 6), 10)
  if (isNaN(year) || isNaN(month)) return key
  const shifted = new Date(year, month - 1 + months, 1)
  return `${shifted.getFullYear()}${String(shifted.getMonth() + 1).padStart(2, "0")}`
}

const monthsBetweenKeys = (from: string, to: string) => {
  const fromYear = parseInt(from.slice(0, 4), 10)
  const fromMonth = parseInt(from.slice(4, 6), 10)
  const toYear = parseInt(to.slice(0, 4), 10)
  const toMonth = parseInt(to.slice(4, 6), 10)
  if ([fromYear, fromMonth, toYear, toMonth].some(isNaN)) return 0
  return (toYear - fromYear) * 12 + (toMonth - fromMonth)
}

// Every progress map on the row is optional in practice; normalise so callers can index freely.
const progressValues = (values: UserAdvanceProgressValues | null | undefined): UserAdvanceProgressValues => ({
  royalties: values?.royalties || {},
  projections: values?.projections || {},
  advances: values?.advances || {}
})

// ---- small components -------------------------------------------------------

const Badge: FC<{ text: string; bg: string; color: string }> = ({ text, bg, color }) => (
  <span
    style={{
      backgroundColor: bg,
      color,
      padding: "3px 10px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.04em",
      whiteSpace: "nowrap"
    }}
  >
    {text}
  </span>
)

const KpiCard: FC<{ label: string; value: string; subtext?: string; subtextColor?: string }> = ({
  label,
  value,
  subtext,
  subtextColor = TEXT_MUTED
}) => (
  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col gap-1.5">
    <KSpan text={label} fontSize={11} fontWeight={600} color={TEXT_MUTED} letterSpacing="0.05em" />
    <KTitleSpan text={value} fontSize={22} fontWeight={700} color={TEXT_PRIMARY} />
    {subtext ? <KSpan text={subtext} fontSize={12} color={subtextColor} /> : null}
  </div>
)

const ProgressBar: FC<{ label: string; pctValue: number; color: string }> = ({ label, pctValue, color }) => (
  <div className="flex flex-col gap-1.5">
    <div className="flex justify-between items-center">
      <KSpan text={label} fontSize={11} fontWeight={600} color={TEXT_MUTED} letterSpacing="0.05em" />
      <KSpan text={`${Math.round(pctValue)}%`} fontSize={13} fontWeight={700} color={TEXT_PRIMARY} />
    </div>
    <div style={{ height: 10, background: "#f1f5f9", borderRadius: 999 }}>
      <div style={{ width: `${Math.min(100, pctValue)}%`, height: "100%", background: color, borderRadius: 999 }} />
    </div>
  </div>
)

const Milestone: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex flex-col gap-1 items-center text-center">
    <KSpan text={label} fontSize={10} fontWeight={600} color={TEXT_MUTED} letterSpacing="0.04em" />
    <KSpan text={value} fontSize={12} fontWeight={600} color={TEXT_PRIMARY} />
  </div>
)

const thStyle: React.CSSProperties = {
  textAlign: "right",
  padding: "10px 12px",
  fontSize: 11,
  fontWeight: 600,
  color: TEXT_MUTED,
  letterSpacing: "0.03em",
  borderBottom: "1px solid #e5e7eb"
}

const tdStyle: React.CSSProperties = {
  padding: "10px 12px",
  fontSize: 13,
  color: TEXT_PRIMARY,
  borderBottom: "1px solid #f1f5f9",
  whiteSpace: "nowrap",
  textAlign: "right"
}

// ---- monthly performance chart ----------------------------------------------

// Inline plugin: shades the "kahuna revenue phase" (the window between company
// recoup and artist recoup) and draws dashed recoup marker lines. Registered
// per-chart via the `plugins` prop so it never leaks into the other charts that
// share the global ChartJS registry.
const recoupPhasePlugin = {
  id: "recoupPhase",
  beforeDatasetsDraw(chart: any) {
    const opts = chart.options.plugins?.recoupPhase
    if (!opts) return
    const { ctx, chartArea, scales } = chart
    const { companyIdx, artistIdx } = opts
    if (companyIdx === null || artistIdx === null || companyIdx === undefined || artistIdx === undefined) return
    const x1 = scales.x.getPixelForValue(Math.min(companyIdx + 1, artistIdx + 1))
    const x2 = chartArea.right

    ctx.save()
    ctx.fillStyle = "rgba(34,197,94,0.09)"
    ctx.fillRect(x1, chartArea.top, x2 - x1, chartArea.bottom - chartArea.top)
    if (x2 - x1 > 28) {
      ctx.translate((x1 + x2) / 2, (chartArea.top + chartArea.bottom) / 2)
      ctx.rotate(-Math.PI / 2)
      ctx.fillStyle = "rgba(21,128,61,0.55)"
      ctx.font = `700 10px ${CHART_FONT}`
      ctx.textAlign = "center"
      ctx.textBaseline = "middle"
      ctx.fillText("KAHUNA REVENUE PHASE", 0, 0)
    }
    ctx.restore()
  },
  afterDatasetsDraw(chart: any) {
    const opts = chart.options.plugins?.recoupPhase
    if (!opts) return
    const { ctx, chartArea, scales } = chart
    const drawMarker = (idx: number | null, color: string, label: string) => {
      if (idx === null || idx === undefined) return
      const x = scales.x.getPixelForValue(idx)
      ctx.save()
      ctx.setLineDash([10, 15])
      ctx.lineWidth = 1.5
      ctx.strokeStyle = color
      ctx.beginPath()
      ctx.moveTo(x, chartArea.top)
      ctx.lineTo(x, chartArea.bottom)
      ctx.stroke()
      ctx.setLineDash([])
      ctx.translate(x + 5, chartArea.bottom - 6)
      ctx.rotate(-Math.PI / 2)
      ctx.fillStyle = color
      ctx.font = `600 9px ${CHART_FONT}`
      ctx.textAlign = "left"
      ctx.textBaseline = "middle"
      ctx.fillText(label, 0, 0)
      ctx.restore()
    }
    drawMarker(opts.companyIdx + 1, "#22c55e70", "Company Recoup")
    drawMarker(opts.artistIdx + 1, "#11111152", "Artist Recoup Est.")
  }
}

const MonthlyPerformanceChart: FC<{ selectedDeal: AdvanceDealRow }> = ({ selectedDeal }) => {
  const months = [...monthKeys(selectedDeal.royalties), ...monthKeys(selectedDeal.projections)]
  const labels = months.map((m) => monthYearShort(m))
  const reportedThrough = latestMonth(selectedDeal.royalties)
  const projectedThrough = latestMonth(selectedDeal.projections)
  // Copied, not aliased: the chart seeds a synthetic "000000" point for the advance itself
  // and must not write that into the row the breakdown table renders from.
  const artistBalance = {
    ...progressValues(selectedDeal.artistBalance),
    royalties: { ...progressValues(selectedDeal.artistBalance).royalties }
  }

  if (months.length === 0) {
    return (
      <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <KTitleSpan text="Monthly Performance" fontSize={16} fontWeight={700} />
        <div className="mt-6 mb-4">
          <KSpan
            text="No reported royalties or projections for this advance yet. The chart appears once the first royalty period lands."
            fontSize={13}
            color={TEXT_MUTED}
          />
        </div>
      </div>
    )
  }

  const findIdx = (key: string | null) => {
    if (!key) return null
    const i = months.findIndex((m) => m === key)
    return i >= 0 ? i : null
  }

  const companyIdx = findIdx(selectedDeal.companyRecoupDate)
  const artistIdx = findIdx(selectedDeal.artistRecoupDate)

  // Dash the projected tail of each line.
  const dashSegment = (ctx: any) => {
    return reportedThrough !== null && reportedThrough < months[ctx.p1DataIndex] ? [5, 4] : undefined
  }

  const cashflowMap: Record<string, number> = {}

  // 1. Add royalties (positive)
  let firstMonth = ""
  Object.entries(selectedDeal.royalties ?? {}).forEach(([month, value]) => {
    if (!firstMonth || month < firstMonth) {
      firstMonth = month
    }
    cashflowMap[month] = (cashflowMap[month] || 0) + (value || 0)
  })

  // 2. Add projections (positive)
  Object.entries(selectedDeal.projections ?? {}).forEach(([month, value]) => {
    cashflowMap[month] = (cashflowMap[month] || 0) + (value || 0)
  })

  // 3. Add advances (negative)
  Object.entries(selectedDeal.advances ?? {}).forEach(([month, value]) => {
    const monthToUse = month < firstMonth ? firstMonth : month
    cashflowMap[monthToUse] = (cashflowMap[monthToUse] || 0) - (value || 0)
  })

  const cashflow: Record<string, number> = {}
  let currentCash = 0

  Object.keys(cashflowMap)
    .sort()
    .forEach((month) => {
      currentCash += cashflowMap[month]
      if (currentCash > 0) currentCash = 0
      cashflow[month] = currentCash
    })

  months.unshift("000000")
  labels.unshift("Adv")
  cashflow["000000"] = selectedDeal.advance * -1
  artistBalance.royalties["000000"] = selectedDeal.advance * -1

  const data = {
    labels,
    datasets: [
      {
        label: "GROSS ROYALTIES",
        data: months.map((m) => {
          if (selectedDeal.royalties[m] !== undefined) return selectedDeal.royalties[m]
          else return selectedDeal.projections[m]
        }),
        borderColor: AMBER,
        backgroundColor: "rgba(245,158,11,0.08)",
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2,
        yAxisID: "y1",
        segment: { borderDash: dashSegment }
      },
      {
        label: "COMPANY OUT. BALANCE",
        data: months.map((m) => cashflow[m]),
        borderColor: GREEN,
        backgroundColor: "rgba(124,58,237,0.05)",
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2,
        yAxisID: "y",
        segment: { borderDash: dashSegment }
      },
      {
        label: "ARTIST OUT. BALANCE",
        data: months.map((m) => {
          if (artistBalance.royalties[m] !== undefined) return artistBalance.royalties[m]
          else return artistBalance.projections[m]
        }),
        borderColor: "#11111192",
        backgroundColor: "rgba(34,197,94,0.05)",
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2,
        yAxisID: "y",
        segment: { borderDash: dashSegment }
      }
    ]
  }
  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "top",
        align: "center",
        labels: {
          boxWidth: 22,
          boxHeight: 2,
          usePointStyle: false,
          font: { family: CHART_FONT, size: 11, weight: "600" },
          color: TEXT_MUTED
        }
      },
      tooltip: { callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${compactUsd(ctx.parsed.y)}` } },
      // chartjs-plugin-datalabels is registered globally by other charts; a
      // per-point label on a 48-month, 3-line trend chart is unreadable, so we
      // turn it off here and surface exact values via the hover tooltip.
      datalabels: { display: false },
      recoupPhase: { companyIdx, artistIdx }
    },
    scales: {
      x: { grid: { display: false }, ticks: { maxTicksLimit: 10, font: { family: CHART_FONT, size: 10 } } },
      y: {
        position: "right",
        title: {
          display: true,
          text: "Outstanding Balance",
          font: { family: CHART_FONT, size: 10 },
          color: TEXT_MUTED
        },
        ticks: { callback: (v: any) => compactUsd(Number(v)), font: { family: CHART_FONT, size: 11 } },
        grid: { color: "#f1f5f9" }
      },
      y1: {
        position: "left",
        beginAtZero: true,
        title: { display: true, text: "Gross Royalties", font: { family: CHART_FONT, size: 10 }, color: TEXT_MUTED },
        ticks: { callback: (v: any) => compactUsd(Number(v)), font: { family: CHART_FONT, size: 11 } },
        grid: { drawOnChartArea: false }
      }
    }
  }
  return (
    <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <KTitleSpan text="Monthly Performance" fontSize={16} fontWeight={700} />
      <KSpan
        text={`Reported through ${monthYear(reportedThrough)} · Projected through ${monthYear(projectedThrough)}`}
        fontSize={12}
        color={TEXT_MUTED}
      />
      <div style={{ height: 360, marginTop: 16 }}>
        <Line data={data} options={options} plugins={[recoupPhasePlugin]} />
      </div>
    </div>
  )
}

// ---- single tranche performance ---------------------------------------------

// The combined chart above draws artist-level revenue against a pooled balance, which cannot
// answer "how is Advance #2 doing". This draws one tranche: what the waterfall applied to it
// each month, and the company / artist balances that remain on it.
const TranchePerformanceChart: FC<{ selectedDeal: AdvanceDealRow; tranche: Tranche }> = ({
  selectedDeal,
  tranche
}) => {
  const reportedThrough = latestMonth(selectedDeal.royalties)
  const months = sortedUnion(tranche.company.balance, tranche.artist.balance, tranche.company.applied)

  if (months.length === 0) {
    return (
      <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <KTitleSpan text={`Advance #${tranche.number} Performance`} fontSize={16} fontWeight={700} />
        <div className="mt-6 mb-4">
          <KSpan
            text="Nothing has been applied to this advance yet."
            fontSize={13}
            color={TEXT_MUTED}
          />
        </div>
      </div>
    )
  }

  const axis = [tranche.fundedMonth, ...months.filter((month) => month !== tranche.fundedMonth)].sort()
  const labels = axis.map((month) => monthYearShort(month))
  const dashSegment = (ctx: any) =>
    reportedThrough !== null && reportedThrough < axis[ctx.p1DataIndex] ? [5, 4] : undefined

  // Balances are drawn below the axis, matching the combined chart's sign convention. Months
  // with no entry carry the previous balance forward — before the first entry that is the
  // full tranche, after the last it simply stays flat.
  const balanceSeries = (balance: { [month: string]: number }) => {
    let carried = tranche.advance
    return axis.map((month) => {
      if (balance[month] !== undefined) carried = balance[month]
      return -carried
    })
  }

  const data = {
    labels,
    datasets: [
      {
        label: "APPLIED TO THIS ADVANCE",
        data: axis.map((month) => tranche.company.applied[month] ?? 0),
        borderColor: AMBER,
        backgroundColor: "rgba(245,158,11,0.08)",
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2,
        yAxisID: "y1",
        segment: { borderDash: dashSegment }
      },
      {
        label: "COMPANY OUT. BALANCE",
        data: balanceSeries(tranche.company.balance),
        borderColor: GREEN,
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2,
        yAxisID: "y",
        segment: { borderDash: dashSegment }
      },
      {
        label: "ARTIST OUT. BALANCE",
        data: balanceSeries(tranche.artist.balance),
        borderColor: "#11111192",
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2,
        yAxisID: "y",
        segment: { borderDash: dashSegment }
      }
    ]
  }

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "top",
        align: "center",
        labels: {
          boxWidth: 22,
          boxHeight: 2,
          usePointStyle: false,
          font: { family: CHART_FONT, size: 11, weight: "600" },
          color: TEXT_MUTED
        }
      },
      tooltip: { callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${compactUsd(ctx.parsed.y)}` } },
      datalabels: { display: false }
    },
    scales: {
      x: { grid: { display: false }, ticks: { maxTicksLimit: 10, font: { family: CHART_FONT, size: 10 } } },
      y: {
        position: "right",
        title: {
          display: true,
          text: "Outstanding Balance",
          font: { family: CHART_FONT, size: 10 },
          color: TEXT_MUTED
        },
        ticks: { callback: (v: any) => compactUsd(Number(v)), font: { family: CHART_FONT, size: 11 } },
        grid: { color: "#f1f5f9" }
      },
      y1: {
        position: "left",
        beginAtZero: true,
        title: { display: true, text: "Applied", font: { family: CHART_FONT, size: 10 }, color: TEXT_MUTED },
        ticks: { callback: (v: any) => compactUsd(Number(v)), font: { family: CHART_FONT, size: 11 } },
        grid: { drawOnChartArea: false }
      }
    }
  }

  return (
    <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <KTitleSpan text={`Advance #${tranche.number} Performance`} fontSize={16} fontWeight={700} />
      <KSpan
        text={`Funded ${monthYear(tranche.fundedMonth)} · reported through ${monthYear(reportedThrough)} · ${
          tranche.recouped ? "company recouped" : `${usd0(tranche.company.outstanding)} outstanding`
        }`}
        fontSize={12}
        color={TEXT_MUTED}
      />
      <div style={{ height: 360, marginTop: 16 }}>
        <Line data={data} options={options} />
      </div>
    </div>
  )
}

// ---- expected vs actual recoup ----------------------------------------------

// Portfolio Overview grades every deal against its recoup target, but the detail page only
// ever showed actuals and projections — so you could see a deal was flagged without seeing
// why. This is the target itself: the straight line the deal was underwritten to reach, drawn
// against collections actually reported. Deliberately no projections; the point is whether
// the deal is keeping up with its promise, not what the forecast hopes for.
const TargetVsActualChart: FC<{ selectedDeal: AdvanceDealRow; tranche?: Tranche }> = ({ selectedDeal, tranche }) => {
  const { targetRecoupMonths } = selectedDeal
  // With a tranche selected the same question is asked of that tranche alone: its own funding
  // month, its share of the target, and only the collections the waterfall applied to it.
  const dealDate = tranche ? tranche.fundedMonth : selectedDeal.dealDate
  const recoupTarget = tranche ? tranche.recoupTarget : selectedDeal.recoupTarget
  const collections = tranche ? tranche.company.applied : selectedDeal.royalties
  // Reported-through is a property of the royalty data, not of a tranche, so it is the same
  // cut-off either way.
  const reportedThrough = latestMonth(selectedDeal.royalties)

  if (!dealDate || !recoupTarget || !targetRecoupMonths || targetRecoupMonths <= 0) return null

  const monthsReported = reportedThrough ? monthsBetweenKeys(dealDate, reportedThrough) : 0
  // Always show the full target window, plus any overrun, so the finish line stays on screen.
  const spanMonths = Math.max(targetRecoupMonths, monthsReported) + 1
  const axis = Array.from({ length: spanMonths + 1 }, (_, index) => addMonthsToKey(dealDate, index))

  let cumulative = 0
  const actual: (number | null)[] = []
  axis.forEach((month) => {
    cumulative += collections?.[month] ?? 0
    actual.push(reportedThrough !== null && month <= reportedThrough ? cumulative : null)
  })

  const expected = axis.map((_, index) => Math.min(recoupTarget, (recoupTarget * index) / targetRecoupMonths))

  const actualToDate = cumulative
  const expectedToDate = Math.min(recoupTarget, (recoupTarget * Math.max(monthsReported, 0)) / targetRecoupMonths)
  const gap = actualToDate - expectedToDate
  const attainment = expectedToDate > 0 ? (actualToDate / expectedToDate) * 100 : null

  // Positive means recouping ahead of the target. At artist level the engine already reports
  // this; for one tranche it comes from that tranche's own recoup lag.
  const timingDelta = tranche
    ? tranche.company.recoupLag === null
      ? null
      : targetRecoupMonths - tranche.company.recoupLag
    : selectedDeal.recoupTimingDelta

  const data = {
    labels: axis.map((month) => monthYearShort(month)),
    datasets: [
      {
        label: "EXPECTED (TARGET PACE)",
        data: expected,
        borderColor: TEXT_MUTED,
        borderDash: [6, 4],
        tension: 0,
        pointRadius: 0,
        borderWidth: 2
      },
      {
        label: "ACTUAL COLLECTED",
        data: actual,
        borderColor: gap >= 0 ? GREEN : RED,
        backgroundColor: gap >= 0 ? "rgba(34,197,94,0.10)" : "rgba(239,68,68,0.10)",
        fill: true,
        tension: 0.25,
        pointRadius: 0,
        borderWidth: 2,
        spanGaps: false
      }
    ]
  }

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "top",
        align: "center",
        labels: {
          boxWidth: 22,
          boxHeight: 2,
          usePointStyle: false,
          font: { family: CHART_FONT, size: 11, weight: "600" },
          color: TEXT_MUTED
        }
      },
      tooltip: { callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${compactUsd(ctx.parsed.y)}` } },
      datalabels: { display: false }
    },
    scales: {
      x: { grid: { display: false }, ticks: { maxTicksLimit: 10, font: { family: CHART_FONT, size: 10 } } },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Cumulative Collections",
          font: { family: CHART_FONT, size: 10 },
          color: TEXT_MUTED
        },
        ticks: { callback: (v: any) => compactUsd(Number(v)), font: { family: CHART_FONT, size: 11 } },
        grid: { color: "#f1f5f9" }
      }
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 min-w-0">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="flex flex-col">
          <KTitleSpan
            text={tranche ? `Target vs Actual Recoup · Advance #${tranche.number}` : "Target vs Actual Recoup"}
            fontSize={16}
            fontWeight={700}
          />
          <KSpan
            text={`Underwritten to collect ${usd0(recoupTarget)} within ${targetRecoupMonths} months of ${monthYear(
              dealDate
            )}`}
            fontSize={12}
            color={TEXT_MUTED}
          />
        </div>
        <Badge
          text={gap >= 0 ? `${usd0(gap)} AHEAD OF TARGET` : `${usd0(Math.abs(gap))} BEHIND TARGET`}
          bg={gap >= 0 ? "#dcfce7" : "#fee2e2"}
          color={gap >= 0 ? "#15803d" : "#b91c1c"}
        />
      </div>
      <div style={{ height: 260, marginTop: 16 }}>
        <Line data={data} options={options} />
      </div>
      <div className="flex flex-wrap gap-3 mt-4">
        <SummaryTile label="EXPECTED TO DATE" value={usd0(expectedToDate)} />
        <SummaryTile label="ACTUAL TO DATE" value={usd0(actualToDate)} />
        <SummaryTile label="ATTAINMENT" value={attainment === null ? "N/A" : pct(attainment)} />
        <SummaryTile label="TIMING" value={timingText(timingDelta)} />
      </div>
    </div>
  )
}

const SummaryTile: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex-1 min-w-[120px] p-3.5 border border-gray-100 rounded-xl bg-gray-50 text-center">
    <KSpan text={label} fontSize={11} fontWeight={600} color={TEXT_MUTED} letterSpacing="0.05em" />
    <div className="mt-1">
      <KTitleSpan text={value} fontSize={18} fontWeight={700} color={TEXT_PRIMARY} />
    </div>
  </div>
)

// ---- monthly breakdown table ------------------------------------------------

const MonthlyBreakdownTable: FC<{ selectedDeal: AdvanceDealRow }> = ({ selectedDeal }) => {
  const [showAll, setShowAll] = useState(false)

  const projectionMonths = monthKeys(selectedDeal.projections)
  const royaltiesMonths = monthKeys(selectedDeal.royalties)

  const months = [...royaltiesMonths, ...projectionMonths]

  const sortedAllMonths = [...months].sort((a, b) => (a < b ? 1 : -1))
  const sortedRoyaltiesMonths = [...royaltiesMonths].sort((a, b) => (a < b ? 1 : -1))

  const visibleMonths = showAll ? sortedAllMonths : sortedRoyaltiesMonths

  const renderAggragateRow = (
    userAdvanceProgressValues: UserAdvanceProgressValues | null | undefined,
    month: string,
    color = false
  ) => {
    const { royalties, projections } = progressValues(userAdvanceProgressValues)
    let value = undefined
    let valutStr = "-"
    if (royalties[month] !== undefined) {
      valutStr = `${replaceCurrencies("USD")} ${addCommasToNumber(royalties[month].toFixed(2))}`
      value = royalties[month]
    } else if (projections[month] !== undefined) {
      valutStr = `${replaceCurrencies("USD")} ${addCommasToNumber(projections[month].toFixed(2))}`
      value = projections[month]
    }

    let colorStyle = {}
    if (color && value !== undefined) colorStyle = { color: value >= 0 ? "green" : "red" }

    return <td style={{ ...tdStyle, ...colorStyle }}>{valutStr}</td>
  }

  const convertToCumulativeMap = (userAdvanceProgressValues: UserAdvanceProgressValues) => {
    // 1. Add royalties (positive)
    const cashflowMap: Record<string, number> = {}

    let firstMonth = ""
    Object.entries(userAdvanceProgressValues.royalties ?? {}).forEach(([month, value]) => {
      if (!firstMonth || month < firstMonth) {
        firstMonth = month
      }
      cashflowMap[month] = (cashflowMap[month] || 0) + (value || 0)
    })

    // 2. Add projections (positive)
    Object.entries(userAdvanceProgressValues.projections ?? {}).forEach(([month, value]) => {
      cashflowMap[month] = (cashflowMap[month] || 0) + (value || 0)
    })

    // 3. Add advances (negative)
    Object.entries(userAdvanceProgressValues.advances ?? {}).forEach(([month, value]) => {
      const monthToUse = month < firstMonth ? firstMonth : month
      cashflowMap[monthToUse] = (cashflowMap[monthToUse] || 0) - (value || 0)
    })

    // 4. Convert to sorted array format
    const cashflow: Record<string, number> = {}
    let currentCash = 0

    Object.keys(cashflowMap)
      .sort()
      .forEach((month) => {
        currentCash += cashflowMap[month]
        cashflow[month] = currentCash
      })

    return { royalties: cashflow, advances: {}, projections: {} }
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <KTitleSpan text="Monthly Breakdown" fontSize={16} fontWeight={700} />
      <div style={{ overflowX: "auto", marginTop: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, textAlign: "left" }}>Month</th>
              <th style={thStyle}>Gross Collections</th>
              <th style={thStyle}>Kahuna Revenue</th>
              <th style={thStyle}>Advance Recoup Applied</th>
              <th style={thStyle}>Artist Payable</th>
              <th style={thStyle}>New Advance Funded</th>
              <th style={thStyle}>Outstanding Advance Balance (EoM)</th>
              <th style={thStyle}>Kahuna Net Cash Flow for IRR</th>
              <th style={thStyle}>Kahuna Balance</th>
            </tr>
          </thead>
          <tbody>
            {visibleMonths.map((month) => {
              const advanceTimelineMap = {
                royalties: selectedDeal.advancesTimeline || {},
                projections: {},
                advances: {}
              }
              const kahunaBalanceMap = {
                royalties: selectedDeal.royalties || {},
                projections: selectedDeal.projections || {},
                advances: selectedDeal.advances || {}
              }
              return (
                <tr key={month} style={{ opacity: projectionMonths.includes(month) ? 0.6 : 1 }}>
                  <td style={{ ...tdStyle, textAlign: "left" }}>
                    {monthYear(month)}
                    {projectionMonths.includes(month) && <KSpan color={TEXT_MUTED} fontSize={11} text=" · proj" />}
                  </td>
                  {renderAggragateRow(selectedDeal.actualGrossAndProjection, month)}
                  {renderAggragateRow(selectedDeal.kahunaRevenue, month)}
                  {renderAggragateRow(selectedDeal.kahunaAdvanceCollection, month)}
                  {renderAggragateRow(selectedDeal.artistPayable, month)}
                  {renderAggragateRow(advanceTimelineMap, month)}
                  {renderAggragateRow(selectedDeal.artistBalance, month, true)}
                  {renderAggragateRow(selectedDeal.kahunaTotal, month)}
                  {renderAggragateRow(convertToCumulativeMap(kahunaBalanceMap), month, true)}
                </tr>
              )
            })}
            {visibleMonths.length === 0 && (
              <tr>
                <td style={{ ...tdStyle, textAlign: "center", color: TEXT_MUTED }} colSpan={9}>
                  No monthly data
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {sortedAllMonths.length > sortedRoyaltiesMonths.length && (
        <div className="flex justify-center mt-3">
          <KButton
            text={showAll ? "Hide projections" : `Show projections`}
            background="transparent"
            shadowDisabled
            onClick={() => setShowAll(!showAll)}
          />
        </div>
      )}
    </div>
  )
}

// ---- tranche history --------------------------------------------------------

const postRecoupDurationText = (value: number | null) => {
  if (value === null || value === undefined) return "N/A"
  // Backend stores the duration as a plain number of months; append the unit.
  return /^\d+$/.test(String(value).trim()) ? `${value * 12} Months` : String(value)
}

// The buffer is a ratio; it was being printed straight as a percentage, so a 43% buffer
// rendered as "0.43%".
const recoupBufferText = (advance: number, recoupTarget: number) => {
  const bufferAmount = recoupTarget - advance
  if (!advance) return usd0(bufferAmount)
  return `${pct((bufferAmount / advance) * 100)} (${usd0(bufferAmount)})`
}

const Tab: FC<{ label: string; sublabel: string; active: boolean; onClick: () => void }> = ({
  label,
  sublabel,
  active,
  onClick
}) => (
  <button
    onClick={onClick}
    className={`px-3 py-2 rounded-lg text-left transition-colors border ${
      active ? "bg-white border-black" : "bg-transparent border-transparent hover:border-gray-200"
    }`}
  >
    <div className="flex flex-col">
      <KSpan text={label} fontSize={12} fontWeight={active ? 700 : 500} color={TEXT_PRIMARY} />
      <KSpan text={sublabel} fontSize={11} color={TEXT_MUTED} />
    </div>
  </button>
)

// The selector the whole page hangs off: pick the combined position or one funded tranche.
const TrancheSelector: FC<{
  tranches: Tranche[]
  selected: number | null
  onSelect: (trancheNumber: number | null) => void
}> = ({ tranches, selected, onSelect }) => {
  if (tranches.length < 2) return null

  return (
    <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-gray-100">
      <Tab
        label="All Advances"
        sublabel={`${tranches.length} combined`}
        active={selected === null}
        onClick={() => onSelect(null)}
      />
      {tranches.map((tranche) => (
        <Tab
          key={tranche.number}
          label={`Advance #${tranche.number}`}
          sublabel={`${monthYear(tranche.fundedMonth)} · ${usd0(tranche.advance)}`}
          active={selected === tranche.number}
          onClick={() => onSelect(tranche.number)}
        />
      ))}
    </div>
  )
}

// Kept alongside the selector: the combined view still benefits from seeing when each tranche
// landed and how the funded total built up.
const TrancheList: FC<{ tranches: Tranche[] }> = ({ tranches }) => {
  if (tranches.length < 2) return null

  let runningTotal = 0
  return (
    <div>
      <KSpan text="Advance History" fontSize={11} fontWeight={700} color={TEXT_MUTED} letterSpacing="0.04em" />
      <div className="mt-1.5 flex flex-col gap-1">
        {tranches.map((tranche) => {
          runningTotal += tranche.advance
          return (
            <div key={tranche.number} className="flex justify-between items-center gap-3">
              <KSpan
                text={`#${tranche.number} · ${monthYear(tranche.fundedMonth)}`}
                fontSize={12}
                color={TEXT_MUTED}
              />
              <div className="flex gap-2 items-center">
                <KSpan text={usd0(tranche.advance)} fontSize={12} fontWeight={600} color={TEXT_PRIMARY} />
                <KSpan text={`(cum. ${usd0(runningTotal)})`} fontSize={11} color={TEXT_MUTED} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const TrancheHistory: FC<{
  selectedDeal: AdvanceDealRow
  tranches: Tranche[]
  activeTranche?: Tranche
  onSelectTranche: (trancheNumber: number | null) => void
}> = ({ selectedDeal, tranches, activeTranche, onSelectTranche }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col gap-4">
      <KTitleSpan text="Deal Offer" fontSize={16} fontWeight={700} lineHeight="20px" />
      <TrancheSelector
        tranches={tranches}
        selected={activeTranche ? activeTranche.number : null}
        onSelect={onSelectTranche}
      />
      <div className="border border-gray-100 rounded-xl p-4 flex flex-col gap-3 bg-gray-50">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                background: PURPLE,
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0
              }}
            >
              {activeTranche ? activeTranche.number : selectedDeal.advanceCount}
            </div>
            <div className="flex flex-col">
              {/* Without a tranche selected this card describes every advance at once, so it
                  says how many rather than "Advance # 3", which read as the third one. */}
              <KSpan
                text={
                  activeTranche
                    ? `Advance # ${activeTranche.number}`
                    : selectedDeal.advanceCount > 1
                    ? `${selectedDeal.advanceCount} Advances (combined)`
                    : "Advance # 1"
                }
                fontSize={14}
                fontWeight={700}
                color={TEXT_PRIMARY}
              />
              <KSpan
                text={`${!activeTranche && selectedDeal.advanceCount > 1 ? "FIRST FUNDED" : "FUNDED"} ${yyyymmToDate(
                  activeTranche ? activeTranche.fundedMonth : selectedDeal.dealDate
                )
                  .toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric"
                  })
                  .toUpperCase()}`}
                fontSize={11}
                fontWeight={500}
                color={TEXT_MUTED}
                letterSpacing="0.03em"
              />
            </div>
          </div>
          {(activeTranche ? activeTranche.recouped : selectedDeal.recouped) ? (
            <Badge text="RECOUPED" bg="#dcfce7" color="#15803d" />
          ) : (
            <Badge text="OUTSTANDING" bg="#fef3c7" color="#b45309" />
          )}
        </div>
        <div className="flex flex-col gap-3">
          <div>
            <KSpan text="Funding Terms" fontSize={11} fontWeight={700} color={TEXT_MUTED} letterSpacing="0.04em" />
            <div className="mt-1.5 flex flex-col gap-1">
              <Row label="Advance Amount" value={usd0(activeTranche ? activeTranche.advance : selectedDeal.advance)} />
              <Row
                label="Recoup Buffer"
                value={recoupBufferText(
                  activeTranche ? activeTranche.advance : selectedDeal.advance,
                  activeTranche ? activeTranche.recoupTarget : selectedDeal.recoupTarget
                )}
              />
              <Row
                label="Recoup Target"
                value={usd0(activeTranche ? activeTranche.recoupTarget : selectedDeal.recoupTarget)}
              />
            </div>
          </div>
          {activeTranche ? null : <TrancheList tranches={tranches} />}
          <div>
            <KSpan text="Deal Terms" fontSize={11} fontWeight={700} color={TEXT_MUTED} letterSpacing="0.04em" />
            {tranches.length > 1 && (
              <div className="mt-0.5">
                {/* Contract model, commissions and exclusivity are stored on the artist, not on
                    the individual advance, so they are identical for every tranche. Saying so
                    beats letting the reader assume the selector changed them. */}
                <KSpan
                  text="Contract terms are held at artist level and apply to every advance."
                  fontSize={11}
                  color={TEXT_MUTED}
                />
              </div>
            )}
            <div className="mt-1.5 flex flex-col gap-1">
              <Row label="Contract Model" value={selectedDeal.contractModel} />
              {selectedDeal.contractModel === "post_recoup" ? (
                <Row label="Post-Recoup Duration" value={postRecoupDurationText(selectedDeal.postRecoupDuration)} />
              ) : (
                <Row label="Fixed Term" value={`${(selectedDeal.contractDuration || 0) * 12} Months`} />
              )}
              <Row label="Current Commission" value={selectedDeal.commission?.toString()} />
              <div style={{ borderBottom: "1px dashed grey" }} />
              <Row
                label="Pre-Recoup Commission"
                value={selectedDeal.userExpense?.preRecoupCommission?.toString() || "-"}
              />
              <Row
                label="Post-Recoup Commission"
                value={selectedDeal.userExpense?.postRecoupCommission?.toString() || "-"}
              />
              <Row label="Distribution Fee" value={selectedDeal.userExpense?.distributionFee?.toString() || "-"} />
              <Row
                label="Post-Recoup Dist. Fee"
                value={selectedDeal.userExpense?.distributionFeePostRecoup?.toString() || "-"}
              />
              <div style={{ borderBottom: "1px dashed grey" }} />
              {!selectedDeal.dealOffer ? (
                <Row label="No Deal Attached" value="" />
              ) : (
                <>
                  <Row label="Exclusivity" value={selectedDeal.dealOffer?.contractExclusivity ? "Yes" : "No"} />
                  <Row
                    label="New Releases"
                    value={selectedDeal.dealOffer?.newReleaseIncludeRecoup ? "Included" : "Not included"}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const Row: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between items-center gap-3">
    <KSpan text={label} fontSize={12} color={TEXT_MUTED} />
    <KSpan text={value} fontSize={12} fontWeight={600} color={TEXT_PRIMARY} />
  </div>
)

// ---- CSV export -------------------------------------------------------------

const exportMonthlyCsv = (selectedDeal?: AdvanceDealRow) => {
  if (!selectedDeal) return

  const projectionMonths = monthKeys(selectedDeal.projections)
  const royaltiesMonths = monthKeys(selectedDeal.royalties)
  const allMonths = [...royaltiesMonths, ...projectionMonths].sort((a, b) => (a < b ? 1 : -1))

  const getVal = (userAdvanceProgressValues: UserAdvanceProgressValues | null | undefined, month: string): string => {
    const { royalties, projections } = progressValues(userAdvanceProgressValues)
    let value: number | undefined
    if (royalties[month] !== undefined) {
      value = royalties[month]
    } else if (projections[month] !== undefined) {
      value = projections[month]
    }
    return value !== undefined ? value.toFixed(2) : ""
  }

  const convertToCumulativeMap = (userAdvanceProgressValues: UserAdvanceProgressValues) => {
    const cashflowMap: Record<string, number> = {}
    let firstMonth = ""
    Object.entries(userAdvanceProgressValues.royalties ?? {}).forEach(([month, value]) => {
      if (!firstMonth || month < firstMonth) firstMonth = month
      cashflowMap[month] = (cashflowMap[month] || 0) + (value || 0)
    })
    Object.entries(userAdvanceProgressValues.projections ?? {}).forEach(([month, value]) => {
      cashflowMap[month] = (cashflowMap[month] || 0) + (value || 0)
    })
    Object.entries(userAdvanceProgressValues.advances ?? {}).forEach(([month, value]) => {
      const monthToUse = month < firstMonth ? firstMonth : month
      cashflowMap[monthToUse] = (cashflowMap[monthToUse] || 0) - (value || 0)
    })
    const cashflow: Record<string, number> = {}
    let currentCash = 0
    Object.keys(cashflowMap)
      .sort()
      .forEach((month) => {
        currentCash += cashflowMap[month]
        cashflow[month] = currentCash
      })
    return { royalties: cashflow, advances: {}, projections: {} }
  }

  const kahunaBalanceMap = {
    royalties: selectedDeal.royalties || {},
    projections: selectedDeal.projections || {},
    advances: selectedDeal.advances || {}
  }
  const cumulativeMap = convertToCumulativeMap(kahunaBalanceMap)

  const header = [
    "Month",
    "Gross Collections",
    "Kahuna Revenue",
    "Advance Recoup Applied",
    "Artist Payable",
    "New Advance Funded",
    "Outstanding Advance Balance (EoM)",
    "Kahuna Net Cash Flow for IRR",
    "Kahuna Balance"
  ]

  const lines = allMonths.map((month) => {
    const advanceTimelineMap = { royalties: selectedDeal.advancesTimeline || {}, projections: {}, advances: {} }
    const isProjection = projectionMonths.includes(month)
    return [
      `${monthYear(month)}${isProjection ? " · proj" : ""}`,
      getVal(selectedDeal.actualGrossAndProjection, month),
      getVal(selectedDeal.kahunaRevenue, month),
      getVal(selectedDeal.kahunaAdvanceCollection, month),
      getVal(selectedDeal.artistPayable, month),
      getVal(advanceTimelineMap, month),
      getVal(selectedDeal.artistBalance, month),
      getVal(selectedDeal.kahunaTotal, month),
      getVal(cumulativeMap, month)
    ].join(",")
  })

  const csv = [header.join(","), ...lines].join("\n")
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = `deal_${selectedDeal.artistName.replace(/\s+/g, "_")}_monthly.csv`
  a.click()
  URL.revokeObjectURL(url)
}

// ---- main -------------------------------------------------------------------

// The engine reads "last 30 days" off the second-most-recent reported month, since the newest
// one is usually still filling up. Same month, but only what landed on this tranche.
const trancheRecoupLast30D = (selectedDeal: AdvanceDealRow, tranche: Tranche) => {
  const reported = monthKeys(selectedDeal.royalties).sort()
  if (reported.length === 0) return 0
  return tranche.company.applied[reported[Math.max(reported.length - 2, 0)]] ?? 0
}

const AdvanceDealDetailPage: FC<AdvanceDealDetailPageProps> = ({ selectedDeal, onBack }) => {
  const [loading, setLoading] = useState(false)
  // null == the combined artist position, which stays the default so the page opens the way
  // it always has.
  const [selectedTranche, setSelectedTranche] = useState<number | null>(null)

  const tranches = useMemo(
    () =>
      selectedDeal
        ? buildTranches({
            advances: selectedDeal.advances || {},
            royalties: selectedDeal.royalties || {},
            projections: selectedDeal.projections || {},
            artistCollections: progressValues(selectedDeal.kahunaAdvanceCollection),
            recoupTarget: selectedDeal.recoupTarget
          })
        : [],
    [selectedDeal]
  )

  const activeTranche = tranches.find((tranche) => tranche.number === selectedTranche)

  if (loading) {
    return (
      <div className="p-8 flex justify-center items-center min-h-[400px]">
        <Loading />
      </div>
    )
  }

  if (!selectedDeal) {
    return (
      <div className="px-10 pt-5 pb-10 flex flex-col gap-4">
        <KButton width="100px" onClick={() => onBack()} text="← Back" background="transparent" shadowDisabled />
        <KSpan text="No deal data available for this artist." color="#666" />
      </div>
    )
  }

  return (
    // min-w-0 so the wide monthly breakdown scrolls inside its own container rather than
    // stretching the page past the viewport.
    <div className="px-10 pt-5 pb-10 flex flex-col gap-6 min-w-0 max-w-full">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-3 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <button
            className="text-xl text-gray-500 hover:text-gray-800"
            onClick={onBack}
            title="Back to Portfolio Overview"
          >
            ←
          </button>
          {selectedDeal.artistImage ? (
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: `center / cover no-repeat url(${selectedDeal.artistImage})`
              }}
            />
          ) : null}
          <KTitleSpan text={selectedDeal.artistName} fontSize={24} fontWeight={700} color={TEXT_PRIMARY} />
          {selectedDeal.recouped ? (
            <Badge text="RECOUPED" bg="#dcfce7" color="#15803d" />
          ) : (
            <Badge text="PERFORMING" bg="#f9fcdc" color="#767676" />
          )}
          {selectedDeal.isNewDeal && !selectedDeal.recouped ? (
            <Badge text="NEW - EXCLUDED FROM RISK" bg="#ede9fe" color="#5b21b6" />
          ) : null}
          <Badge text="ADV" bg="#ede9fe" color="black" />
        </div>
        <div>
          <KButton text="Export" background="black" textColor="white" onClick={() => exportMonthlyCsv(selectedDeal)} />
        </div>
      </div>

      {/* KPI cards — every figure here is decomposable per tranche, so the selector rewrites
          them rather than leaving artist-level numbers under a tranche heading. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
        <KpiCard
          label={activeTranche ? "ADVANCE AMOUNT" : "TOTAL DEPLOYED"}
          value={usd0(activeTranche ? activeTranche.advance : selectedDeal.advance)}
        />
        <KpiCard
          label="OUTSTANDING"
          value={usd0(activeTranche ? activeTranche.company.outstanding : selectedDeal.outstanding)}
          subtext={`${pct(
            activeTranche
              ? activeTranche.advance > 0
                ? (activeTranche.company.outstanding / activeTranche.advance) * 100
                : 0
              : selectedDeal.outstandingPctOfDeployed
          )} of deployed`}
        />
        <KpiCard
          label="RECOUP TARGET"
          value={usd0(activeTranche ? activeTranche.recoupTarget : selectedDeal.recoupTarget)}
          subtext={`Recoup multiplier ${multipleX(selectedDeal.recoupMultiplier)}`}
        />
        <KpiCard
          label={activeTranche ? "FUNDED" : "DEAL DATE"}
          value={monthYear(activeTranche ? activeTranche.fundedMonth : selectedDeal.dealDate)}
        />
        <KpiCard
          label="RECOUP LAST 30D"
          value={usd0(activeTranche ? trancheRecoupLast30D(selectedDeal, activeTranche) : selectedDeal.recoupLast30D)}
          subtext="Company-side"
        />
        {/* Same IRR family and wording as Portfolio Overview, so a figure means the same
            thing on both screens. Under a tranche these are solved from that tranche's own
            cashflow: its funding outflow, then the collections applied to it. */}
        <KpiCard
          label="12M IRR"
          value={xirrPct(activeTranche ? windowedIrr(activeTranche.normalizedCashflow, 12) : selectedDeal.irr12M)}
          subtext="Annualized · first 12 months"
        />
        <KpiCard
          label="24M IRR"
          value={xirrPct(activeTranche ? windowedIrr(activeTranche.normalizedCashflow, 24) : selectedDeal.irr24M)}
          subtext="Annualized · first 24 months"
        />
        <KpiCard
          label="36M IRR"
          value={xirrPct(activeTranche ? annualizedIrr(activeTranche.normalizedCashflow) : selectedDeal.irr36M)}
          subtext="Annualized · first 36 months"
        />
        <KpiCard
          label="LIFETIME XIRR"
          value={xirrPct(activeTranche ? trancheXirr(activeTranche.datedCashflow) : selectedDeal.xirr)}
          subtext="Annualized · all cash flows to date"
        />
        <KpiCard
          label="ECONOMIC MOIC"
          value={multipleX(activeTranche ? trancheEconomicMoic(activeTranche) : selectedDeal.economicMoic)}
          subtext="Collected to date ÷ deployed"
        />
      </div>

      {/* Recoup progress */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col gap-5 min-w-0">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <KTitleSpan
            text={activeTranche ? `Recoup Progress · Advance #${activeTranche.number}` : "Recoup Progress"}
            fontSize={16}
            fontWeight={700}
          />
          <div className="flex gap-2 flex-wrap">
            <Badge
              text={`Company Recoup: ${monthsText(
                activeTranche ? activeTranche.company.recoupLag : selectedDeal.companyRecoupLag
              )}`}
              bg={GREEN}
              color="white"
            />
            <Badge
              text={`Artist Recoup: ${monthsText(
                activeTranche ? activeTranche.artist.recoupLag : selectedDeal.artistRecoupLag
              )}`}
              bg={TEXT_PRIMARY}
              color="white"
            />
          </div>
        </div>
        <ProgressBar
          label="COMPANY RECOUP"
          pctValue={activeTranche ? activeTranche.company.recoupPct : selectedDeal.companyRecoupPct}
          color={GREEN}
        />
        <ProgressBar
          label="ARTIST RECOUP"
          pctValue={activeTranche ? activeTranche.artist.recoupPct : selectedDeal.artistRecoupPct}
          color={TEXT_PRIMARY}
        />
        <div className="flex justify-between flex-wrap gap-4 pt-2 border-t border-gray-100">
          <Milestone
            label={activeTranche ? "FUNDED" : "DEAL START"}
            value={monthYear(activeTranche ? activeTranche.fundedMonth : selectedDeal.dealDate)}
          />
          <Milestone
            label="TODAY"
            value={new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" })}
          />
          <Milestone
            label="COMPANY RECOUP"
            value={monthYear(activeTranche ? activeTranche.company.recoupMonth : selectedDeal.companyRecoupDate)}
          />
          <Milestone
            label="ARTIST RECOUP"
            value={monthYear(activeTranche ? activeTranche.artist.recoupMonth : selectedDeal.artistRecoupDate)}
          />
          {/* Contract end is driven by the artist's last recoupment, not by one tranche. */}
          <Milestone label="CONTRACT END" value={monthYear(selectedDeal.contractEnd)} />
        </div>
      </div>

      {/* Target vs actual — the deal's own version of the pace Portfolio Overview grades it on */}
      <TargetVsActualChart selectedDeal={selectedDeal} tranche={activeTranche} />

      {/* Monthly performance + deal detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start min-w-0">
        {activeTranche ? (
          <TranchePerformanceChart selectedDeal={selectedDeal} tranche={activeTranche} />
        ) : (
          <MonthlyPerformanceChart selectedDeal={selectedDeal} />
        )}

        <div className="lg:col-span-1">
          <TrancheHistory
            selectedDeal={selectedDeal}
            tranches={tranches}
            activeTranche={activeTranche}
            onSelectTranche={setSelectedTranche}
          />
        </div>
      </div>

      {/* Monthly breakdown — the waterfall itself runs at artist grain, so this table stays
          combined whatever the selector says. Labelled rather than silently left stale. */}
      {activeTranche ? (
        <div className="flex flex-col gap-2">
          <KSpan
            text="The monthly breakdown below covers all advances combined — the royalty waterfall is calculated at artist level."
            fontSize={12}
            color={TEXT_MUTED}
          />
          <MonthlyBreakdownTable selectedDeal={selectedDeal} />
        </div>
      ) : (
        <MonthlyBreakdownTable selectedDeal={selectedDeal} />
      )}
    </div>
  )
}

export default AdvanceDealDetailPage
